import { useState } from "react";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ConfirmationModal from "@/components/ConfirmationModal";
import { createAddOnProjectReq } from "@/api/projectsApi";
import { getErrorMessage } from "@/utils/getErrorMessage";

type Props = {
  projectId: number;
  onError: (message: string) => void;
};

/**
 * SDCP-1340 — admin creates an add-on project: a second project on the same account (e.g. a
 * battery added to a site that already has a project). It starts as a copy of this project at
 * Pending Review with no incentive; the admin then edits its system details and cost.
 */
const CreateAddOnProjectButton = ({ projectId, onError }: Props) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const response = await createAddOnProjectReq(projectId);
      setOpen(false);
      navigate(`/projects/${response.data.project.id}`);
    } catch (error: unknown) {
      setOpen(false);
      const specific = (
        error as { response?: { data?: { error?: string } } } | undefined
      )?.response?.data?.error;
      onError(
        specific ||
          getErrorMessage(error, "Failed to create the add-on project"),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        onClick={() => setOpen(true)}
        disabled={isSubmitting}
        sx={{
          height: 48,
          borderRadius: "8px",
          fontWeight: 600,
          textTransform: "none",
        }}
      >
        Create add-on project
      </Button>
      <ConfirmationModal
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={handleConfirm}
        title="Create add-on project?"
        message={`This creates a new project for the same customer account, linked to project #${projectId}. It starts at Pending Review with no incentive, using this project's details. Edit its system information and total cost, then calculate its incentive. The $10,000 cap is shared across both projects.`}
        confirmText="Create add-on"
      />
    </>
  );
};

export default CreateAddOnProjectButton;
